import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, clearToken } from "../App";
import { Button } from "../components/ui/button";
import { ArrowLeft, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { AvatarDisplay, ICON_MAP } from "../components/SettingsWidgets";
import { devError } from "../utils/devLogger";
import {
  ProfileSection,
  LanguageSection,
  NotificationsSection,
  SmartNudgesSection,
  DisplaySection,
  DataPrivacySection,
  AccountActionsSection
} from "../components/SettingsSections";

const Settings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, reminderRes, avatarRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/reminders/status`),
        axios.get(`${API}/avatars`)
      ]);
      setSettings(settingsRes.data.settings);
      setProfile(settingsRes.data.profile);
      setPushSubscribed(reminderRes.data.push_subscribed);
      setAvatars(avatarRes.data.avatars);
    } catch (error) {
      devError("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    setPushSupported("Notification" in window && "serviceWorker" in navigator);
    axios.get(`${API}/notifications/preferences`).then(res => setNotifPrefs(res.data)).catch(() => {});
  }, [fetchSettings]);

  const updateNotifPref = async (key, value) => {
    try {
      await axios.put(`${API}/notifications/preferences`, { [key]: value });
      setNotifPrefs(prev => ({ ...prev, [key]: value, ...(key === "daily_nudges" && value ? { paused: false } : {}) }));
      toast.success(t("settings.saved"));
    } catch { toast.error("Failed to update preference"); }
  };

  const updateSetting = async (key, value) => {
    try {
      const res = await axios.put(`${API}/settings`, { [key]: value });
      setSettings(res.data.settings);
      toast.success(t("settings.saved"));
    } catch { toast.error("Failed to update setting"); }
  };

  const setFitnessGoal = async (goalId) => {
    try {
      const res = await axios.put(`${API}/settings/profile`, { fitness_goal: goalId });
      setProfile(res.data.profile);
      toast.success("Fitness goal updated!");
    } catch { toast.error("Failed to update goal"); }
  };

  const handleAvatarSelect = async (avatarId) => {
    try {
      const res = await axios.put(`${API}/settings/profile/avatar`, { avatar_id: avatarId });
      setProfile(res.data.profile);
      setShowAvatarPicker(false);
      toast.success("Avatar updated!");
    } catch { toast.error("Failed to update avatar"); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/settings/profile/picture`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(res.data.profile);
      setShowAvatarPicker(false);
      toast.success("Profile picture updated!");
    } catch { toast.error("Failed to upload picture"); }
  };

  const handleLanguageSync = async (lang) => {
    i18n.changeLanguage(lang);
    await updateSetting("language", lang);
  };

  const subscribePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { toast.error(t("settings.notificationsDenied")); return; }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: "BLaC-hL2Nkx2QHGI4F6B6FZJxEi3L9FCSCdrIqDYSEZ9M3-eLqzHIDQkJXPEVjo69y2bDEEjRRkcY1UZ8sR3p0"
      });
      await axios.post(`${API}/reminders/subscribe`, { subscription: subscription.toJSON() });
      setPushSubscribed(true);
      toast.success(t("settings.notificationsEnabled"));
    } catch { toast.error(t("settings.notificationsFailed")); }
  };

  const unsubscribePush = async () => {
    try {
      await axios.delete(`${API}/reminders/unsubscribe`);
      setPushSubscribed(false);
      toast.success(t("settings.notificationsDisabled"));
    } catch { toast.error("Failed to unsubscribe"); }
  };

  const deleteAccount = async () => {
    try {
      await axios.delete(`${API}/settings/account`);
      toast.success("Account deleted");
      navigate("/");
    } catch { toast.error("Failed to delete account"); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { devError("Logout:", e); } finally { clearToken(); navigate("/"); }
  };

  const languages = [
    { code: "en", flag: "\ud83c\uddec\ud83c\udde7", label: "English" },
    { code: "de", flag: "\ud83c\udde9\ud83c\uddea", label: "Deutsch" },
    { code: "fr", flag: "\ud83c\uddeb\ud83c\uddf7", label: "Fran\u00e7ais" },
    { code: "es", flag: "\ud83c\uddea\ud83c\uddf8", label: "Espa\u00f1ol" },
    { code: "ru", flag: "\ud83c\uddf7\ud83c\uddfa", label: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439" },
    { code: "uk", flag: "\ud83c\uddfa\ud83c\udde6", label: "\u0423\u043a\u0440\u0430\u0457\u043d\u0441\u044c\u043a\u0430" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="settings-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600 hover:text-sky-800" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t("common.dashboard")}
            </Button>
            <div className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t("settings.title")}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <ProfileSection
            profile={profile} setProfile={setProfile} avatars={avatars} t={t}
            showAvatarPicker={showAvatarPicker} setShowAvatarPicker={setShowAvatarPicker}
            onPhotoUpload={handlePhotoUpload} onAvatarSelect={handleAvatarSelect} onSetFitnessGoal={setFitnessGoal}
          />
          <LanguageSection languages={languages} currentLang={i18n.language} onLanguageSync={handleLanguageSync} t={t} />
          <NotificationsSection
            settings={settings} updateSetting={updateSetting}
            pushSupported={pushSupported} pushSubscribed={pushSubscribed}
            onSubscribe={subscribePush} onUnsubscribe={unsubscribePush} t={t}
          />
          {notifPrefs && <SmartNudgesSection notifPrefs={notifPrefs} updateNotifPref={updateNotifPref} />}
          <DisplaySection settings={settings} updateSetting={updateSetting} t={t} />
          <DataPrivacySection t={t} navigate={navigate} />
          <AccountActionsSection
            onLogout={handleLogout} onDelete={deleteAccount}
            showDeleteConfirm={showDeleteConfirm} setShowDeleteConfirm={setShowDeleteConfirm} t={t}
          />
        </div>
      </main>
    </div>
  );
};

export { AvatarDisplay, ICON_MAP };
export default Settings;
